"use client";

import BasicPagination from "@/components/common/BasicPagination";
import ListingSearchItem from "@/components/listing/ListingSearchItem";
import SavedListingsProvider from "@/components/listing/SavedListingsProvider";
import { Skeleton } from "@/components/ui/skeleton";
import type { ListingSearchData } from "@/lib/types/search";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";

const PAGE_SIZE = 24;

type Props = {
  ownerId: string;
  ownerName: string;
  locale: string;
  cityId?: string;
  cityName?: string;
};

type ListingResponse = {
  success: boolean;
  data: {
    items: ListingSearchData[];
    count: number;
  };
};

const getInitialPage = (value: string | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const OwnerListingResults = ({
  ownerId,
  ownerName,
  locale,
  cityId,
  cityName,
}: Props) => {
  const t = useTranslations("owner");
  const searchParams = useSearchParams();
  const [page, setPage] = useState(() =>
    getInitialPage(searchParams.get("page")),
  );
  const sectionRef = useRef<HTMLElement>(null);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["listing", "owner", ownerId, cityId ?? null, locale, page],
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({
        locale,
        ownerId,
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
        sort: "recommended",
      });

      if (cityId) {
        params.set("cityId", cityId);
      }

      const response = await fetch(
        `/api/site/public/listing?${params.toString()}`,
      );
      const payload = (await response.json()) as ListingResponse;

      if (!response.ok || !payload.success) {
        throw new Error("Failed to load owner listings");
      }

      return payload.data;
    },
  });

  const listings = data?.items ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.ceil(count / PAGE_SIZE);

  const updatePage = (nextPage: number) => {
    const params = new URLSearchParams(window.location.search);

    if (nextPage > 1) {
      params.set("page", nextPage.toString());
    } else {
      params.delete("page");
    }

    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`,
    );
    setPage(nextPage);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section ref={sectionRef} className="flex scroll-mt-6 flex-col gap-6">
      {isLoading ? (
        <Skeleton className="h-9 w-80 max-w-full" />
      ) : (
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {cityName
            ? t("city-listings-page.grid-title", { city: cityName, count })
            : t("listings-page.grid-title", { count })}
        </h2>
      )}

      {isError ? (
        <p className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
          {t("listings-page.load-error")}
        </p>
      ) : (
        <SavedListingsProvider
          listingIds={listings.map((listing) => listing.id)}
        >
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3"
            aria-busy={isFetching}
          >
            {isLoading
              ? Array.from({ length: 9 }, (_, index) => (
                  <Skeleton key={index} className="h-40 rounded-2xl" />
                ))
              : listings.map((listing) => (
                  <ListingSearchItem key={listing.href} item={listing} />
                ))}
          </div>
        </SavedListingsProvider>
      )}

      {!isLoading && !isError && listings.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <h3 className="font-semibold">{t("listings-page.empty.title")}</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("listings-page.empty.description", { name: ownerName })}
          </p>
        </div>
      )}

      {!isLoading && !isError && totalPages > 1 && (
        <BasicPagination
          currentPage={page}
          perPage={PAGE_SIZE}
          totalPages={totalPages}
          onPageChange={updatePage}
        />
      )}
    </section>
  );
};

export default OwnerListingResults;
